import { memo, type JSX } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { MASTER_STRIP } from '../../../audio/api';
import type { AudioStrip, Id, MonitoringMode } from '../../../core/model';
import type { MutateOptions } from '../../../state/api';
import { Icon } from '../../components/Icon';
import type { MenuEntry } from '../../components/menuModel';
import { Slider } from '../../components/Slider';
import { useProject, useUiContext } from '../../context';
import { useEditSession } from '../../hooks/useEditSession';
import { attempt } from '../../safe';
import { faderToGain, formatDb, gainToDb, gainToFader, UNITY_POSITION } from './fader';
import { Meter } from './Meter';

export const MONITORING_LABELS: Record<MonitoringMode, string> = {
  off: 'Monitor off',
  'monitor-only': 'Monitor only (mute output)',
  'monitor-and-output': 'Monitor and output',
};

interface StripView {
  name: string;
  volume: number;
  muted: boolean;
  monitoring: MonitoringMode | null;
}

/** One mixer channel. `sourceId` null is the master bus. */
export const Strip = memo(function Strip({ sourceId }: { sourceId: Id | null }): JSX.Element | null {
  const { studio, ui } = useUiContext();
  const { commands } = studio;
  const view = useProject(
    useShallow((p): StripView | null => {
      if (sourceId === null) return { name: 'Master', volume: p.audio.masterVolume, muted: p.audio.masterMuted, monitoring: null };
      const source = p.sources[sourceId];
      return source ? { name: source.name, volume: source.audio.volume, muted: source.audio.muted, monitoring: source.audio.monitoring } : null;
    }),
  );
  const session = useEditSession(studio);
  if (!view) return null;

  const patch = (strip: Partial<AudioStrip>, options?: MutateOptions): void => {
    attempt(studio, () => {
      if (sourceId !== null) commands.setAudioStrip(sourceId, strip, options);
      else {
        commands.setAudioSettings(
          { ...(strip.volume !== undefined ? { masterVolume: strip.volume } : {}), ...(strip.muted !== undefined ? { masterMuted: strip.muted } : {}) },
          options,
        );
      }
    });
  };

  // A fader move is one undo step however long the drag lasts.
  const moveFader = (position: number): void =>
    session.edit(
      'volume',
      () => {
        const project = studio.project.getState().project;
        return sourceId === null ? project.audio.masterVolume : (project.sources[sourceId]?.audio.volume ?? 1);
      },
      (volume, options) => patch({ volume }, options),
      faderToGain(position),
    );
  const commitFader = (): void => session.commit();

  const monitoringMenu = (): MenuEntry[] =>
    (Object.keys(MONITORING_LABELS) as MonitoringMode[]).map((mode) => ({
      label: MONITORING_LABELS[mode],
      checked: view.monitoring === mode,
      onSelect: () => patch({ monitoring: mode }),
    }));

  const contextMenu = (): MenuEntry[] => [
    ...(sourceId !== null
      ? ([
          { label: 'Filters', onSelect: () => ui.getState().openDialog({ type: 'filters', owner: { type: 'source', id: sourceId } }) },
          { label: 'Properties', onSelect: () => ui.getState().openDialog({ type: 'properties', sourceId }) },
          { label: 'Monitoring', submenu: monitoringMenu() },
          { separator: true },
        ] satisfies MenuEntry[])
      : []),
    { label: view.muted ? 'Unmute' : 'Mute', onSelect: () => patch({ muted: !view.muted }) },
    { label: 'Reset volume to 0 dB', onSelect: () => patch({ volume: 1 }) },
    { separator: true },
    { label: 'Advanced audio properties…', onSelect: () => ui.getState().openDialog({ type: 'advanced-audio' }) },
  ];

  const openMenu = (x: number, y: number, items: MenuEntry[], returnFocus: HTMLElement | null): void =>
    ui.getState().openMenu({ x, y, items, returnFocus });

  const db = gainToDb(view.volume);
  return (
    <section
      className={sourceId === null ? 'strip master' : 'strip'}
      aria-label={`${view.name} audio`}
      onContextMenu={(e) => {
        e.preventDefault();
        openMenu(e.clientX, e.clientY, contextMenu(), null);
      }}
    >
      <div className="strip-name ui-ellipsis" title={view.name}>
        {view.name}
      </div>
      <div className="strip-db ui-mono" aria-live="off">
        {formatDb(db)}
      </div>
      <div className="strip-body">
        <Meter stripId={sourceId ?? MASTER_STRIP} label={`${view.name} level meter`} />
        <Slider
          vertical
          label={`${view.name} volume`}
          value={gainToFader(view.volume)}
          min={0}
          max={1}
          bigStep={0.05}
          valueText={formatDb(db)}
          marks={[UNITY_POSITION]}
          onChange={moveFader}
          onCommit={commitFader}
          onReset={() => patch({ volume: 1 })}
        />
      </div>
      <div className="strip-buttons">
        <button
          type="button"
          className={view.muted ? 'ui-icon-btn alert' : 'ui-icon-btn'}
          aria-label={view.muted ? `Unmute ${view.name}` : `Mute ${view.name}`}
          aria-pressed={view.muted}
          title={view.muted ? 'Unmute' : 'Mute'}
          onClick={() => patch({ muted: !view.muted })}
        >
          <Icon name={view.muted ? 'speakerOff' : 'speaker'} />
        </button>
        {sourceId !== null ? (
          <>
            <button
              type="button"
              className={view.monitoring !== 'off' ? 'ui-icon-btn on' : 'ui-icon-btn'}
              aria-label={`${view.name} monitoring`}
              aria-haspopup="menu"
              title={view.monitoring ? MONITORING_LABELS[view.monitoring] : 'Monitoring'}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                openMenu(rect.left, rect.bottom + 2, monitoringMenu(), e.currentTarget);
              }}
            >
              <Icon name="headphones" />
            </button>
            <button
              type="button"
              className="ui-icon-btn"
              aria-label={`${view.name} audio filters`}
              title="Filters"
              onClick={() => ui.getState().openDialog({ type: 'filters', owner: { type: 'source', id: sourceId } })}
            >
              <Icon name="sliders" />
            </button>
          </>
        ) : null}
        <button
          type="button"
          className="ui-icon-btn"
          aria-label={`${view.name} more options`}
          aria-haspopup="menu"
          title="More"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            openMenu(rect.left, rect.bottom + 2, contextMenu(), e.currentTarget);
          }}
        >
          <Icon name="dots" />
        </button>
      </div>
    </section>
  );
});
